import { clone, equals, omit, path } from 'ramda'

import * as RoutingAPI from 'api/rules'
import { isTempId } from 'utils'

import { routingRuleIdField } from './helpers'

const isNew = (ruleOrGroup) => isTempId(ruleOrGroup.id)
export const rulePointsToGroup = (rule) =>
  path(['action', 'type'], rule) === 'EXECUTE_RULE_GROUP'
export const getGroupIdFromRule = (rule) =>
  path(['action', 'options', 'nextRuleGroupId'], rule)
export const defaultActionGoesToGroup = (draftGroup) =>
  path(['defaultAction', 'type'], draftGroup) === 'EXECUTE_RULE_GROUP'
export const getGroupIdFromDefaultAction = (draftGroup) =>
  path(['defaultAction', 'options', 'nextRuleGroupId'], draftGroup)

const deleteRule = async (rule) => {
  rule.status = 'ARCHIVED'
  await RoutingAPI.patchRule(rule.id, rule, false)
}

const resolveAndSaveRule = async (draftRule, draft, published) => {
  if (!draftRule) {
    return
  }

  // recursive call
  if (rulePointsToGroup(draftRule)) {
    const groupId = await resolveAndSaveGroup(
      draft.ruleGroupsById[getGroupIdFromRule(draftRule)],
      draft,
      published
    )
    draftRule.action.options.nextRuleGroupId = groupId
  }

  if (isNew(draftRule)) {
    const savedRule = await RoutingAPI.postRule(omit(['id'], draftRule), false)
    return savedRule.id
  } else {
    const publishedRule = published.rules.find(
      (rule) => rule.id === draftRule.id
    )

    if (!equals(publishedRule, draftRule)) {
      const updatedRule = await RoutingAPI.patchRule(
        draftRule.id,
        draftRule,
        false
      )
      return updatedRule.id
    } else {
      return publishedRule.id
    }
  }
}

const resolveAndSaveGroup = async (draftGroup, draft, published) => {
  const ruleIds = await Promise.all(
    draftGroup[routingRuleIdField()].map((ruleId) =>
      resolveAndSaveRule(draft.rulesById[ruleId], draft, published)
    )
  )
  draftGroup[routingRuleIdField()] = ruleIds.filter((r) => !!r)

  if (defaultActionGoesToGroup(draftGroup)) {
    const groupId = await resolveAndSaveGroup(
      draft.ruleGroupsById[getGroupIdFromDefaultAction(draftGroup)],
      draft,
      published
    )
    draftGroup.defaultAction.options.nextRuleGroupId = groupId
  }

  if (isNew(draftGroup)) {
    const { data: savedGroup } = await RoutingAPI.createRuleGroup(
      published.ruleTree.id,
      omit(['id'], draftGroup)
    )
    return savedGroup.id
  } else {
    const publishedGroup = published.ruleGroups.find(
      (group) => group.id === draftGroup.id
    )

    if (!equals(publishedGroup, draftGroup)) {
      const { data: updatedGroup } = await RoutingAPI.patchRuleGroup(draftGroup)
      return updatedGroup.id
    } else {
      return publishedGroup.id
    }
  }
}

const updateOnCallRuleIfChanged = async (draft, published) => {
  if (
    !equals(draft.defaultRuleUsers.sort(), published.defaultRuleUsers.sort())
  ) {
    await RoutingAPI.updateOnCallUsers(draft.defaultRuleUsers)
  }
}

const trySaving = async ({ draft, published, onSuccess, onFail }) => {
  const tree = published.ruleTree
  try {
    const rootGroup = draft.ruleGroupsById[tree.rootRuleGroupId]
    await resolveAndSaveGroup(rootGroup, draft, published)
    draft.deletedRules
      .filter((rule) => !isNew(rule))
      .forEach((rule) => deleteRule(rule))
    await updateOnCallRuleIfChanged(draft, published)
    onSuccess()
  } catch (err) {
    console.warn(err)
    onFail(err)
  }
}

const save = ({ draft, published }) =>
  new Promise((resolve, reject) =>
    trySaving({
      draft: clone(draft),
      published: clone(published),
      onSuccess: resolve,
      onFail: reject,
    })
  )

export default save
